import type { ReactNode } from 'react';
import { Link as ReactRouterLink, useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@repo/common';
import type { SDSNavigationAPI, SDSProvidedImageProps, SDSProvidedLinkProps } from '@repo/sds/providers/SDSProvider';
import { SDSProvider } from '@repo/sds/providers/SDSProvider';
import type { SDSLocationAPI } from '@repo/sds/providers/SDSProvider/types';

interface RouterLinkAdapterProps extends SDSProvidedLinkProps {
  href: string;
  prefetch?: 'intent' | 'render' | 'viewport' | 'none';
}

/**
 * Adapter component that makes React Router Link compatible with SDS Provider.
 * Converts href prop to React Router's 'to' prop.
 * Supports prefetch prop with smart default: 'intent' (hover-based).
 */
function RouterLinkAdapter({ href, prefetch, ref, ...props }: RouterLinkAdapterProps) {
  // Default to 'intent' (hover) for good balance of speed vs bandwidth
  // Can be overridden per-link: use 'viewport' for nav, 'intent' for lists
  const prefetchStrategy = prefetch ?? 'intent';

  // In production, HOME is served by the landing app (separate deployment).
  // Use reloadDocument to force full page navigation instead of client-side routing.
  const shouldReloadDocument = href === ROUTES.HOME && !import.meta.env.DEV;

  return (
    <ReactRouterLink to={href} prefetch={prefetchStrategy} reloadDocument={shouldReloadDocument} ref={ref} {...props} />
  );
}

/**
 * Standard HTML img adapter for React Router client app
 * Uses basic img element without optimization (suitable for SPAs)
 */
function StandardImageAdapter({
  src,
  alt,
  className,
  dataTestId,
  onLoad,
  onError,
  style,
  width,
  height,
  loading,
}: SDSProvidedImageProps) {
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      data-testid={dataTestId}
      onLoad={onLoad}
      onError={onError}
      style={style}
      width={width}
      height={height}
      loading={loading}
    />
  );
}

interface ReactRouterSDSProviderProps {
  children: ReactNode;
}

/**
 * Shared SDS Provider configured for React Router applications (Client & Admin)
 */
export function ReactRouterSDSProvider({ children }: ReactRouterSDSProviderProps) {
  const routerNavigate = useNavigate();
  const routerLocation = useLocation();

  const navigation: SDSNavigationAPI = {
    navigate: (path: string) => routerNavigate(path),
    replace: (path: string) => routerNavigate(path, { replace: true }),
    back: () => routerNavigate(-1),
  };

  const location: SDSLocationAPI = {
    pathname: routerLocation.pathname,
  };

  return (
    <SDSProvider
      linkComponent={RouterLinkAdapter}
      imageComponent={StandardImageAdapter}
      navigation={navigation}
      location={location}
    >
      {children}
    </SDSProvider>
  );
}
